import { getActiveWithoutSchedules } from 'api/contract.api';
import { SliceStatus } from 'constants/enums/slices';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { doList, setLastSearch } from 'store/slices/userSlice';
import Contract from 'types/contract';
import { RoleFilter } from '../userList.types';
import { filterUsers } from '../userList.utils';

export const useUserList = () => {
  const dispatch = useAppDispatch();
  const { users, lastUserSearch, status } = useAppSelector(
    (state) => state.user
  );
  const [searchParams] = useSearchParams();
  const [activeContractsWithoutSchedules, setActiveContractsWithoutSchedules] =
    useState<Contract[]>([]);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [showMissingScheduleUsers, setShowMissingScheduleUsers] = useState(
    Boolean(searchParams.get('contratosActivosSinHorarios'))
  );

  useEffect(() => {
    void dispatch(doList());
  }, [dispatch]);

  useEffect(() => {
    setShowMissingScheduleUsers(
      Boolean(searchParams.get('contratosActivosSinHorarios'))
    );
  }, [searchParams]);

  useEffect(() => {
    const loadActiveContractsWithoutSchedules = async () => {
      try {
        const response = await getActiveWithoutSchedules();
        setActiveContractsWithoutSchedules(response.data ?? []);
      } catch {
        setActiveContractsWithoutSchedules([]);
      }
    };

    void loadActiveContractsWithoutSchedules();
  }, []);

  const activeContractUserIds = useMemo(
    () =>
      new Set(
        activeContractsWithoutSchedules.map((contract) => contract.userId)
      ),
    [activeContractsWithoutSchedules]
  );

  const filteredUsers = useMemo(
    () =>
      filterUsers(users, activeContractUserIds, {
        role: roleFilter,
        searchValue: lastUserSearch,
        showInactiveUsers,
        showMissingScheduleUsers,
      }),
    [
      activeContractUserIds,
      lastUserSearch,
      roleFilter,
      showInactiveUsers,
      showMissingScheduleUsers,
      users,
    ]
  );

  const setSearchValue = useCallback(
    (value: string) => {
      dispatch(setLastSearch(value));
    },
    [dispatch]
  );

  return {
    activeContractUserIds,
    activeContractsWithoutSchedulesCount:
      activeContractsWithoutSchedules.length,
    filteredUsers,
    loading: status === SliceStatus.Updating,
    roleFilter,
    searchValue: lastUserSearch,
    setRoleFilter,
    setSearchValue,
    setShowInactiveUsers,
    setShowMissingScheduleUsers,
    showInactiveUsers,
    showMissingScheduleUsers,
  };
};
